// HelpGuides.jsx — Help content for the features added in late September /
// October 2026: Judiciary, Swish IQ (sessions, finalising, team selection,
// coach mode, coach sign-in), People changes, Court layout, Coach Codes,
// BC Registrations, My Portal additions. Help.jsx merges these in:
//   NEW_SECTIONS      – whole new sidebar entries (placed after `after`)
//   REPLACE_SECTIONS  – replace an existing section's content
//   EXTRA_CONTENT     – appended to the end of an existing section
//   RELEASE_NOTES     – shown at the top of Release Notes

import React, { useState } from 'react';

const sectionWrap = { padding: '1.5rem 2rem', maxWidth: 920 };
const extraWrap = { padding: '0 2rem 1.5rem', maxWidth: 920 };
const h2 = { color: '#A4161A', borderBottom: '2px solid #f4cdd0', paddingBottom: 8, marginTop: 0 };
const h3 = { color: '#333', marginTop: '1.6rem', marginBottom: '0.4rem' };
const newH3 = { ...h3, display: 'flex', alignItems: 'center', gap: 8 };
const para = { color: '#444', lineHeight: 1.55, fontSize: '0.95rem', margin: '0 0 0.8rem 0' };
const ul = { color: '#444', lineHeight: 1.6, fontSize: '0.95rem', paddingLeft: '1.4rem', margin: '0 0 0.8rem 0' };
const ol = { ...ul, listStyleType: 'decimal' };
const tipBox = { background: '#fff8e1', border: '1px solid #ffe082', borderLeft: '4px solid #f9a825', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const warnBox = { background: '#ffebee', border: '1px solid #ef9a9a', borderLeft: '4px solid #c62828', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const noteBox = { background: '#e3f2fd', border: '1px solid #90caf9', borderLeft: '4px solid #1565c0', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const code = { background: '#f4f4f4', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: '0.88em' };
const newBadge = { background: '#A4161A', color: 'white', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 10, letterSpacing: '0.04em' };
const swatch = (bg, fg) => ({ display: 'inline-block', background: bg, color: fg, fontWeight: 800, borderRadius: 4, padding: '0 7px', margin: '0 2px' });

// A screenshot from /help/<name>.png. Hidden until the image exists, so a
// guide never shows a broken picture.
function Shot({ name, caption }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return (
    <figure style={{ margin: '0.9rem 0 1.1rem' }}>
      <img
        src={`/help/${name}.png`}
        alt={caption}
        onError={() => setOk(false)}
        style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
      />
      <figcaption style={{ fontSize: '0.8rem', color: '#777', marginTop: 6 }}>{caption}</figcaption>
    </figure>
  );
}

const New = ({ children }) => <h3 style={newH3}><span style={newBadge}>NEW</span>{children}</h3>;

const SWISH_ANY = [
  { pageKey: 'swishiq', sectionKey: 'sessions' },
  { pageKey: 'swishiq', sectionKey: 'players' },
  { pageKey: 'swishiq', sectionKey: 'reports' },
  { pageKey: 'swishiq', sectionKey: 'player-emails' },
  { pageKey: 'swishiq', sectionKey: 'coach-mode' },
];

// ======================= whole new sections =======================
export const NEW_SECTIONS = [
  {
    after: 'game-planner',
    key: 'judiciary',
    label: 'Judiciary',
    group: 'Pages',
    requires: { pageKey: 'judiciary', sectionKey: 'judiciary' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Judiciary</h2>
        <p style={para}>
          The Judiciary tab pulls together every tech foul recorded in Game reports so you can see who
          is accumulating fouls, and lets you raise, track and cancel <strong>suspensions</strong>.
          Suspended players then show on the Game page against every game their team plays while the
          suspension is in force.
        </p>
        <Shot name="judiciary-summary" caption="Judiciary: tech fouls for the last 3 weeks, grouped by competition and team." />

        <h3 style={h3}>Where the data comes from</h3>
        <p style={para}>
          Court supervisors record tech fouls in the <strong>Report</strong> popup on each game (Game page).
          Each tech foul names a player picked from the two teams, the number of fouls (1 or 2), the
          severity (Low or High), whether the player was ejected, and comments. See <em>Game → Game
          reports</em> for how to enter them.
        </p>

        <h3 style={h3}>The summary</h3>
        <ol style={ol}>
          <li>Set the period in <strong>Tech fouls in the last [3] weeks</strong>. It defaults to 3 weeks and counts back from today.</li>
          <li>Players are grouped by <strong>competition</strong>, then <strong>team</strong>, most fouls first.</li>
          <li>Each player row shows total tech fouls, ejections (red), the number of <em>High</em> and <em>Low</em> severity fouls, and every game (date, opponent, fouls, severity, EJECTED). Hover a game line to see the comments.</li>
          <li>The <strong>Suspension</strong> column shows the dates of any current or upcoming suspension, or a <strong>Suspend</strong> button.</li>
        </ol>

        <h3 style={h3}>Raising a suspension</h3>
        <ol style={ol}>
          <li>Click <strong>Suspend</strong> on a player row (player and team are filled in), or <strong>+ Raise suspension</strong> at the top and search for any player by name.</li>
          <li>Check the <strong>Team</strong> the player is suspended from.</li>
          <li>Enter the number of <strong>Weeks</strong> and the <strong>Start date</strong> (defaults to today). The <strong>End date</strong> calculates automatically: start date + weeks × 7 − 1 day (e.g. 3 weeks from Thu 1 Oct ends Wed 21 Oct).</li>
          <li>Tick <strong>Communicated to the player</strong> once they've been told, and enter the <strong>IBA name</strong> of the person who contacted them.</li>
          <li>Add a <strong>Comment</strong> if useful, then <strong>Raise suspension</strong>.</li>
        </ol>
        <Shot name="judiciary-suspension" caption="Raise suspension: weeks and start date give the end date automatically." />

        <h3 style={h3}>Managing suspensions</h3>
        <ul style={ul}>
          <li>The <strong>Suspensions</strong> list shows current and upcoming suspensions. Tick <em>Show finished and cancelled</em> to see older ones.</li>
          <li>The <strong>Communicated</strong> tick box can be changed straight from the list.</li>
          <li><strong>Edit</strong> changes weeks, dates, contact and comment. <strong>Cancel</strong> withdraws a suspension (it stays in history, marked cancelled, and no longer shows on the Game page).</li>
          <li>Every raise, edit and cancel is recorded in the Audit log.</li>
        </ul>

        <h3 style={h3}>On the Game page</h3>
        <p style={para}>
          When either team in a game has a player whose suspension covers that game's date, a red
          <strong> Suspension</strong> button appears to the left of the game's Report button. Click it to
          see who is suspended, which team they play for, and the start and end dates.
        </p>
        <Shot name="game-suspension-button" caption="The red Suspension button on a game, and the list it opens." />
        <div style={noteBox}>
          A player counts for every team they're registered with in Basketball Connect for the current
          season, plus the team recorded on the suspension.
        </div>
        <div style={tipBox}>
          Access to this tab is the <code style={code}>judiciary / judiciary</code> permission. Profiles with
          edit on Admin → Audit received it automatically; grant others under Admin → Security Profiles.
        </div>
      </div>
    )
  },

  // ---------------- Swish IQ ----------------
  {
    after: 'people',
    key: 'swishiq-overview',
    label: 'SwishIQ Trials overview',
    group: 'SwishIQ Trials',
    requires: SWISH_ANY,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>SwishIQ Trials — overview</h2>
        <p style={para}>
          SwishIQ Trials runs representative team trials. A <strong>session</strong> is one set of trials (e.g.
          "REP27 Trials – U14 Boys"); each session has <strong>trial sessions</strong> (the individual
          nights). Coaches record notes on players during each trial session in <strong>coach mode</strong>
          on a shared iPad, admins set outcomes and team selections, then <strong>finalise</strong> each trial
          session to move the right players on to the next.
        </p>
        <h3 style={h3}>The flow</h3>
        <ol style={ol}>
          <li><strong>Create the session</strong> from the trial competition in Admin → BC Registrations. Registered players are added automatically.</li>
          <li><strong>Set it up</strong> under SwishIQ Trials → Sessions → Manage: trial sessions (dates, times, Elimination/Continuation, Allow renumbering), assigned coaches, players, teams and positions.</li>
          <li><strong>Run each trial session</strong> in coach mode: check players in, record notes, pick teams and positions.</li>
          <li><strong>Set outcomes</strong> (Shortlist, Conditional, Selected, Unsuccessful) on the Outcomes page.</li>
          <li><strong>Finalise</strong> the trial session from the Sessions page. Players going through are moved into the next trial session; everyone else becomes Unsuccessful.</li>
          <li><strong>Email</strong> unsuccessful and successful players (Eliminated / Successful tabs) and run the <strong>Team Selections</strong> report.</li>
        </ol>
        <h3 style={h3}>Sub-tabs</h3>
        <ul style={ul}>
          <li><strong>Sessions</strong> — every session with its trial sessions and outcome counts; Manage, Outcome, Email, Archive, Finalise.</li>
          <li><strong>Players</strong> — every trial player (players come from Basketball Connect, so there's no Add player).</li>
          <li><strong>Skills</strong> — the skills and descriptors coaches pick from.</li>
          <li><strong>Reports</strong> and <strong>Team Selections</strong> — player-fit report and team selections report.</li>
          <li><strong>AI Feedback</strong>, <strong>Eliminated</strong>, <strong>Successful</strong>, <strong>Audit Log</strong>.</li>
          <li>The black <strong>Open coach mode</strong> button (top right) opens coach mode for today's trial sessions in a new tab.</li>
        </ul>
        <div style={noteBox}>
          Default teams and positions for new sessions are set in <strong>Admin → SwishIQ Trials</strong>.
        </div>
      </div>
    )
  },
  {
    after: 'swishiq-overview',
    key: 'swishiq-sessions',
    label: 'Sessions & finalising',
    group: 'SwishIQ Trials',
    requires: { pageKey: 'swishiq', sectionKey: 'sessions' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Sessions, setup and finalising</h2>
        <Shot name="swish-sessions" caption="SwishIQ Trials → Sessions: each trial session listed under its session with outcome counts and Finalise Session." />
        <h3 style={h3}>The Sessions list</h3>
        <ul style={ul}>
          <li>Each session shows its Membership Division, gender, head coach and number of <strong>Trial Sessions</strong>.</li>
          <li>Under each session, every trial session is a smaller indented line: <em>Trial 1 · Thu 1 Oct · 6:00pm–8:00pm · Elimination</em>, players and check-ins, and a count for each outcome (e.g. 10 Selected, 5 Shortlist, 3 Unsuccessful, Not set). The counts line up in columns.</li>
          <li>Trial sessions are numbered by date and start time (two at the same time keep the order they were added).</li>
        </ul>

        <h3 style={h3}>Setting up a session (Manage)</h3>
        <p style={para}>The page runs top to bottom: Session details, Sub-sessions, Assigned coaches, Add players, Selection attributes.</p>
        <ul style={ul}>
          <li><strong>Membership Division</strong> is read-only — it's the Basketball Connect registration group the session was created from.</li>
          <li><strong>Team selection</strong>: the teams this session picks for and the positions on each team. Add with the box, remove with ×. Defaults come from Admin → SwishIQ Trials.</li>
          <li><strong>Sub-sessions</strong> (trial sessions): date, start, end, Elimination or Continuation, and <strong>Allow renumbering</strong> (see below).</li>
          <li>Once a trial session is finalised its <strong>date and times are locked</strong>, so it can't be moved after trials that haven't run.</li>
        </ul>
        <Shot name="swish-session-setup" caption="Manage session: Team selection and the Sub-sessions table with Allow renumbering." />

        <h3 style={h3}>Allow renumbering</h3>
        <ul style={ul}>
          <li><strong>Yes</strong> — the first coach to open that trial session in coach mode is asked whether to renumber (Fresh numbers, 1, 2, 3… in the previous order) or keep the numbers.</li>
          <li><strong>No</strong> — the coach isn't asked; players keep their numbers.</li>
          <li>After opening, the column shows what was applied: <em>Renumbered</em> or <em>Numbers kept</em>. The first trial session is never renumbered.</li>
        </ul>

        <h3 style={h3}>Finalise Session</h3>
        <p style={para}>
          Trial sessions finalise in order: only the first trial session that isn't finalised shows the
          <strong> Finalise Session</strong> button. When it's done, the next one gets the button.
        </p>
        <ol style={ol}>
          <li>Make sure outcomes are set on the Outcomes page.</li>
          <li>Click <strong>Finalise Session</strong> on the current trial session.</li>
          <li><strong>Continuation</strong>: everyone goes through. <strong>Elimination</strong>: tick the categories that go through — <em>Selected</em> is ticked by default; add others (or <em>No outcome set</em>) as needed.</li>
          <li>The right-hand side lists everyone who will go through, updating as you tick.</li>
          <li>Tick the red box confirming <em>This closes the session and is not reversible</em>, then <strong>Finalise Session</strong>.</li>
        </ol>
        <Shot name="swish-finalise" caption="Finalise popup: categories on the left, players going through on the right." />
        <div style={warnBox}>
          <strong>Finalising can't be undone.</strong> Players going through are added to the next trial session
          (keeping their number if it's free). Everyone else is set to <strong>Unsuccessful</strong> (their team pick
          is cleared) and removed from later trial sessions unless they've already checked in. The trial
          session's outcomes become read-only, coach mode closes for it (open iPads are signed out of it),
          and its date and times are locked.
        </div>
        <div style={noteBox}>
          After the first finalise, Basketball Connect only adds new registrants to the current trial
          session, and never re-adds players a finalise has already decided.
        </div>
      </div>
    )
  },
  {
    after: 'swishiq-sessions',
    key: 'swishiq-outcomes',
    label: 'Outcomes & team selection',
    group: 'SwishIQ Trials',
    requires: { pageKey: 'swishiq', sectionKey: 'sessions' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Outcomes and team selection</h2>
        <p style={para}>
          Open <strong>Outcome</strong> on a session. It opens on the active trial session (the first one not
          finalised); use the Sub-session picker to look at others.
        </p>
        <Shot name="swish-outcomes" caption="Outcomes: team summary on the left, players with outcomes and team positions on the right." />
        <h3 style={h3}>Setting outcomes</h3>
        <ul style={ul}>
          <li>Each player has a choice of outcome: Shortlist, Conditional, Unsuccessful, then <strong>Selected</strong> (next to the team columns).</li>
          <li>Hover a player's name for their strengths and weaknesses count. A red <strong>✕</strong> means they haven't checked in.</li>
          <li>The row of counts above the list (All players, each outcome, Not set) doubles as a filter — click one to show only those players.</li>
          <li><strong>Set all (shown) to</strong> applies one outcome to every player in the current list (respecting the filter).</li>
          <li>Nothing is saved until you click <strong>Save</strong>.</li>
        </ul>
        <h3 style={h3}>Teams and positions</h3>
        <ul style={ul}>
          <li>Each team has a column; pick a position under a team for the player. Picking a position automatically marks the player <strong>Selected</strong>, and the drop-down turns green.</li>
          <li>A player can only be on one team — other teams are greyed out for them until you clear the first pick.</li>
          <li>Unsuccessful players can't be given a position. Changing a player to any outcome other than Selected clears their pick.</li>
          <li><strong>Selected players must have a team and position</strong> — Save is blocked and the missing rows are highlighted until they do.</li>
        </ul>
        <h3 style={h3}>Email Offer</h3>
        <p style={para}>
          Once the outcome is saved, click <strong>Email Offer</strong> (top right) on the final trial's Outcome page to email the
          players picked for a team. It needs Edit on the Player emails permission.
        </p>
        <ol style={ul}>
          <li>Choose the <strong>team</strong> (e.g. WSC, SJL1, SJL2) and fill out the template: the subject and message can use <code>{'{{FirstName}}'}</code>, <code>{'{{Name}}'}</code>, <code>{'{{Team}}'}</code> and <code>{'{{Position}}'}</code>. Your signature is added to the end. Then <strong>Preview recipients</strong>.</li>
          <li>The list shows the team, each player's number, name, position and the email address the offer goes to. <strong>Preview email</strong> shows exactly what each player will receive. Players with no email address are listed and skipped.</li>
          <li><strong>Extract PDF</strong> and <strong>Extract CSV</strong> download the list at any point.</li>
          <li>Add anyone who should be copied in the <strong>CC</strong> (visible to the player) and <strong>BCC</strong> (hidden) boxes under the message.</li>
          <li><strong>Send…</strong> asks for a final confirmation (team, number of players, who it is sent from and every address), then sends one email to each player.</li>
        </ol>
        <p style={para}>
          The PDF list of the team (with who was sent the offer and the message) is stored against the trial. Every offer sent appears
          under <strong>Offers sent for this trial</strong> at the top of the Outcome page, with a PDF button to download it again, and in the session's Sent emails.
        </p>
        <h3 style={h3}>The team summary</h3>
        <p style={para}>
          The table to the left of the players has teams across the top and positions down the side, with
          a total for each team. Counts are coloured:
          <span style={swatch('#e3f4ea', '#1b7a43')}>2</span> green,
          <span style={swatch('#fff4e0', '#b36b00')}>1 or 3</span> amber,
          <span style={swatch('#fdecec', '#c62828')}>0 or 4+</span> red.
        </p>
        <div style={noteBox}>
          A finalised trial session's outcomes are read-only (green note at the top, no Save). Coaches can
          also pick teams in coach mode — see <em>Coach mode</em>.
        </div>
      </div>
    )
  },
  {
    after: 'swishiq-outcomes',
    key: 'swishiq-reports',
    label: 'Team Selections & emails',
    group: 'SwishIQ Trials',
    requires: [{ pageKey: 'swishiq', sectionKey: 'reports' }, { pageKey: 'swishiq', sectionKey: 'player-emails' }],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Team Selections report and outcome emails</h2>
        <h3 style={h3}>Team Selections</h3>
        <ol style={ol}>
          <li>SwishIQ Trials → <strong>Team Selections</strong>. Choose the session.</li>
          <li>It uses the <strong>final trial</strong> (the latest trial session with selections) unless you choose another trial session.</li>
          <li>One table per team, ordered by position: position, number, name, preferred positions, height, age, date of birth, gender, rep experience, rep level, email, mobile.</li>
          <li><strong>Export CSV</strong> downloads all teams in one file.</li>
        </ol>
        <Shot name="swish-team-selections" caption="Team Selections report." />
        <h3 style={h3}>Eliminated and Successful emails</h3>
        <ul style={ul}>
          <li><strong>Eliminated</strong> emails players who didn't go through; <strong>Successful</strong> emails those who did. Both send one email per player by BCC.</li>
          <li>Choose the session (whole session or one trial session). The <strong>Categories</strong> box lists each outcome with counts:
            on Eliminated the categories that didn't go through are ticked; on Successful the categories that went through are ticked. Untick any you don't want to email.</li>
          <li>Recipients follow the ticked categories; untick individual players if needed. Only ticked players are emailed.</li>
          <li><strong>Include AI feedback</strong> is on by default for Eliminated and off for Successful.</li>
          <li>Merge tags: <code style={code}>{'{{FirstName}}'}</code> and <code style={code}>{'{{AI Feedback block}}'}</code>. Use <strong>Preview</strong> before <strong>Send</strong>.</li>
        </ul>
      </div>
    )
  },
  {
    after: 'swishiq-reports',
    key: 'swishiq-coach-mode',
    label: 'Coach mode (iPad)',
    group: 'SwishIQ Trials',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Coach mode on the iPad</h2>
        <p style={para}>
          Coach mode is where coaches check players in and record notes during a trial session. It opens
          for one trial session, from an hour before it starts until 30 minutes after it ends.
        </p>
        <h3 style={h3}>Getting in</h3>
        <ul style={ul}>
          <li><strong>Coach sign-in page</strong> (recommended for shared iPads): <strong>swishiq.snakepit.com.au</strong>, then <strong>Trials</strong> — see <em>Coach sign-in page</em>.</li>
          <li><strong>My Portal</strong> → Coach tab or Summary → <strong>Launch coach mode</strong> on the trial session.</li>
          <li><strong>SwishIQ Trials tab</strong> → <strong>Open coach mode</strong> (staff), or the trial tile on the Coach Planner.</li>
          <li>Finalised trial sessions never appear and can't be opened.</li>
        </ul>
        <h3 style={h3}>Who's recording? (SMS check)</h3>
        <ol style={ol}>
          <li>Tap your name, then <strong>Start as …</strong>.</li>
          <li>A 6-digit code is sent by SMS to your mobile. Enter it and tap <strong>Verify and start</strong>.</li>
          <li>Notes are saved under your name. <strong>Switch coach</strong> hands the iPad over; a coach already verified on that iPad today isn't asked again.</li>
        </ol>
        <Shot name="coach-sms" caption="Coach mode: choose who's recording. A 6-digit code is then sent to that coach by SMS." />
        <div style={noteBox}>
          A coach who opened coach mode from their own My Portal (or the sign-in page) is already verified.
          Guest coaches need a mobile number on file to verify.
        </div>
        <h3 style={h3}>Starting a later trial session</h3>
        <p style={para}>
          If the trial session allows renumbering, the first coach in is asked to choose <strong>Fresh
          numbers</strong> (renumber 1, 2, 3… in the previous order) or <strong>Keep previous numbers</strong>, then
          <strong> Open session</strong>. If renumbering isn't allowed, numbers are kept automatically.
        </p>
        <h3 style={h3}>The number grid</h3>
        <ul style={ul}>
          <li>Each tile shows the number, first name and, once picked, the team and position (e.g. <strong>WSC · C</strong>).</li>
          <li><strong>Check in</strong>: tick players in and set their positions (G, F, C) and height.</li>
          <li><strong>📷 Photos</strong> to capture player photos; <strong>Current Teams</strong> to see the selections so far.</li>
        </ul>
        <Shot name="coach-grid" caption="The number grid with team picks on the tiles." />
        <h3 style={h3}>Picking a team and position</h3>
        <ol style={ol}>
          <li>Tap a player's tile to open their note screen.</li>
          <li>Tap the player's <strong>name on the black bar</strong> (shown with ▾), choose a team and a position, then <strong>Save</strong>. The player is marked Selected.</li>
          <li><strong>Clear</strong> removes the pick (and the Selected outcome).</li>
          <li>Choosing <strong>Selected</strong> in the notes history status opens the same picker. Only <em>This session</em>'s status can be changed; earlier and later trial sessions show their status as grey text.</li>
        </ol>
        <Shot name="coach-team-picker" caption="Team and position picker in coach mode." />
        <h3 style={h3}>Current Teams</h3>
        <p style={para}>
          Teams across the top, positions down the side, with the players picked for each. Names are
          coloured <span style={swatch('#e3f4ea', '#1b7a43')}>green</span> for 2 at a position,
          <span style={swatch('#fff4e0', '#b36b00')}>amber</span> for 1 or 3 and
          <span style={swatch('#fdecec', '#c62828')}>red</span> for 0 or 4+. The bottom row totals each team.
        </p>
        <Shot name="coach-current-teams" caption="Current Teams in coach mode." />
      </div>
    )
  },
  {
    after: 'swishiq-coach-mode',
    key: 'swishiq-coach-sign-in',
    label: 'Coach sign-in page',
    group: 'SwishIQ Trials',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Coach sign-in page (swishiq.snakepit.com.au)</h2>
        <p style={para}>
          A standard page for the shared iPads. <strong>https://swishiq.snakepit.com.au</strong> opens with two
          buttons: <strong>Trials</strong> (the coach sign-in below) and <strong>Management</strong> (the rostering
          platform at roster.snakepit.com.au). To skip the first screen, bookmark <strong>https://swishiq.snakepit.com.au/trials</strong>
          (also available at roster.snakepit.com.au/coach-login).
        </p>
        <ol style={ol}>
          <li>On swishiq.snakepit.com.au, tap <strong>Trials</strong>.</li>
          <li>Choose your name. Only coaches with a trial session today are listed.</li>
          <li>Tap <strong>Send code</strong>, enter the 6-digit SMS code and tap <strong>Verify</strong>.</li>
          <li>You see today's trial sessions you're assigned to (trial number, time, type, players, head or assistant coach).</li>
          <li>Tap <strong>Open coach mode</strong> (available from an hour before the start; ended ones show <em>Finished</em>). You go straight in as yourself.</li>
          <li>In coach mode, <strong>My sessions</strong> returns to the list. <strong>Not you? Sign out</strong> goes back to the name list.</li>
        </ol>
        <Shot name="coach-sign-in" caption="Coach sign-in: choose your name, verify by SMS, open your trial session." />
        <div style={noteBox}>Sign-in lasts 12 hours or until the browser tab is closed.</div>
      </div>
    )
  },

  // ---------------- Admin additions ----------------
  {
    after: 'admin-referee-codes',
    key: 'admin-coach-codes',
    label: 'Coach Codes',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'coach-codes' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Coach Codes</h2>
        <p style={para}>
          The Coach Planner equivalent of Referee Codes: a small chip in front of each coach on a session,
          such as <strong>HC</strong> (Head Coach) and <strong>AC</strong> (Assistant Coach).
        </p>
        <ul style={ul}>
          <li>Admin → <strong>Coach Codes</strong>: add, rename, reorder or archive codes.</li>
          <li>On the Coach Planner, click the chip next to a coach to cycle: no code → HC → AC → … → no code. One more click after the last code clears it.</li>
          <li>The code shows against the session in My Portal (Coach tab and Summary).</li>
        </ul>
      </div>
    )
  },
  {
    after: 'admin-coach-codes',
    key: 'admin-swishiq',
    label: 'SwishIQ Trials defaults',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'settings' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>SwishIQ Trials defaults</h2>
        <p style={para}>
          Admin → <strong>SwishIQ Trials</strong> holds the default <strong>teams</strong> that trial sessions pick
          players for (WSC, SJL1, SJL2) and the <strong>positions</strong> on each team (PG, SG, SF, PF, C, Other, Dev).
        </p>
        <ul style={ul}>
          <li>Add with the box, reorder with ‹ ›, remove with ×, then <strong>Save</strong>.</li>
          <li>New sessions copy these lists; each session can change its own under SwishIQ Trials → Sessions → Manage.</li>
        </ul>
      </div>
    )
  },
];

// ======================= replaced sections =======================
export const REPLACE_SECTIONS = {
  'court-planner': () => (
    <div style={sectionWrap}>
      <h2 style={h2}>Court</h2>
      <p style={para}>
        The Court Planner rosters court officials (scorers, supervisors, etc.). Each day is one row; a
        person can work more than one shift on the same day.
      </p>
      <Shot name="court-planner" caption="Court: one day per row, shifts with times and cost, and the add row." />
      <h3 style={h3}>Adding a shift</h3>
      <ol style={ol}>
        <li>On the day's row, choose the person in <strong>+ Add court official</strong>.</li>
        <li>Choose one of their roles (only roles they hold are listed; it's picked for you if they have one).</li>
        <li>Enter the start and end time, check the cost shown, then click <strong>Add</strong>.</li>
      </ol>
      <h3 style={h3}>Each shift</h3>
      <ul style={ul}>
        <li>Name and role are coloured green when accepted in My Portal and red when rejected.</li>
        <li>Change times in place; they save once both are filled in.</li>
        <li>The cost is the role's hourly rate × hours (or the flat rate when there are no times). The day header shows the day's total.</li>
        <li>Tick <strong>Attended</strong> after the shift; ✕ removes it.</li>
      </ul>
      <h3 style={h3}>Locking days</h3>
      <p style={para}>Lock a day (small Lock button in the day header) to freeze it, e.g. after payroll runs.</p>
    </div>
  ),
};

// ======================= appended to existing sections =======================
export const EXTRA_CONTENT = {
  'game-planner': () => (
    <div style={extraWrap}>
      <New>Game reports: players, severity and suspensions</New>
      <p style={para}>The <strong>Report</strong> button on each game opens the court supervisor's report.</p>
      <ul style={ul}>
        <li><strong>Tech fouls</strong>: choose the player from the drop-down — both teams' players, grouped by team (from Basketball Connect, matched on the game's division and current season). Use <em>Someone else (type name)</em> for anyone not listed, and pick their team.</li>
        <li>Choose <strong>1 or 2 tech fouls</strong> and the <strong>severity</strong> (Low or High), tick <strong>Ejection</strong> if they were ejected, and add comments.</li>
        <li><strong>Injuries</strong> pick the player the same way.</li>
        <li>Tech fouls feed the <strong>Judiciary</strong> tab. The Court Supervisors report shows the player's team and severity.</li>
        <li>A red <strong>Suspension</strong> button (twice the Report width) appears on games where a team has a suspended player; click it for who, their team and the dates.</li>
      </ul>
      <Shot name="game-report" caption="Game report: tech foul player drop-down grouped by team, with severity." />
    </div>
  ),
  'coach-planner': () => (
    <div style={extraWrap}>
      <New>Coach codes</New>
      <p style={para}>
        Each coach on a session has a small chip (e.g. HC, AC). Click it to cycle through the codes set in
        Admin → Coach Codes; one click past the last code clears it. The code shows in My Portal.
      </p>
    </div>
  ),
  'people': () => (
    <div style={extraWrap}>
      <New>People Management</New>
      <ul style={ul}>
        <li>Tabs: People Management, Referees, Bench Officials, Coaches, Court Officials, Participants (Players), Availability.</li>
        <li><strong>Edit a person</strong>: the window has <strong>Person</strong>, <strong>Roles</strong> (active roles, qualifications), <strong>Availability</strong> (weekly times, Team Assignments and Team Exclusions) and <strong>Bank Details</strong> tabs. Each has Update (saves and closes) and Cancel.</li>
        <li><strong>BasketballConnect match</strong>: a person is linked to their Basketball Connect account when first and last name match and the email or mobile matches. Linked people show the <strong>BasketballConnect ID</strong> first on the Person tab, and their name, email and phone are locked — update them in Basketball Connect and they flow through on the next sync. The list has a BasketballConnect ID column.</li>
        <li><strong>+ Add Person</strong>: choose <strong>Select from BasketballConnect</strong> or <strong>Add manually</strong>. A person added from BasketballConnect is linked to that account straight away.</li>
        <li><strong>Team Exclusions</strong> (Availability tab, and My Portal → Availability): teams whose games the person is never rostered on. Team Assignments moved to the same place.</li>
        <li><strong>BC Match</strong> (next to + Add Person) runs the matching now; it also runs after every sync (5am, 9am, 12pm, 3pm).</li>
        <li>People who are a parent on a child's registration don't match — the account must be registered under their own name.</li>
      </ul>
      <Shot name="people-edit" caption="Edit person: Person, Roles, Availability and Bank Details tabs." />
      <New>Participants (Players)</New>
      <ul style={ul}>
        <li>Participants come from Basketball Connect; there's no Add or Archive.</li>
        <li>For Basketball Connect players, External ID, name, email and mobile are read-only.</li>
        <li><strong>Coaching Programs</strong> is a drop-down of tick boxes. A newly ticked program starts as <em>Financial: No</em>; the sync marks it paid when they pay in Basketball Connect.</li>
        <li>Children's emails arrive from Basketball Connect without the extra ".firstname" suffix.</li>
      </ul>
    </div>
  ),
  'my-portal': () => (
    <div style={extraWrap}>
      <New>Programs, trials and coach codes</New>
      <ul style={ul}>
        <li><strong>Programs</strong>: start typing a participant's name and pick them from the list (contact details fill in), or type a new first and last name. If Basketball Connect later registers the same name for that program, the two records merge and the Basketball Connect one is kept.</li>
        <li><strong>Summary</strong> includes SwishIQ Trials sessions for the week (pink "SwishIQ Trials" badge), with <strong>Launch coach mode</strong> when it's open, and they're in the calendar export.</li>
        <li>The <strong>coach code</strong> (e.g. HC) shows against coaching sessions.</li>
      </ul>
    </div>
  ),
  'admin-coaching-programs': () => (
    <div style={extraWrap}>
      <New>Shared program form</New>
      <p style={para}>
        Creating or editing a coaching program here and from Admin → BC Registrations uses the same form:
        name, owners, weekly budget, signup URL, dates, weekly schedule and linked programs.
      </p>
    </div>
  ),
  'admin-competition-mapping': () => (
    <div style={extraWrap}>
      <New>Create, edit and archive from each competition</New>
      <ul style={ul}>
        <li><strong>Program</strong> competitions: choose the registration group, then <strong>Create coaching program…</strong> opens the full program form and links it. Each linked program has <strong>Edit</strong>, <strong>Archive</strong> (removes its links) and <strong>Unlink</strong>.</li>
        <li><strong>Trial</strong> competitions: <strong>Create SwishIQ Trials session…</strong> opens the New session popup (Membership Division from the registration group), then the full session editor. Linked sessions have <strong>Edit</strong>, <strong>Archive</strong> and <strong>Unlink</strong>.</li>
        <li>The status card lists problems from the latest sync only.</li>
      </ul>
    </div>
  ),
  'admin-bc-integration': () => (
    <div style={extraWrap}>
      <div style={warnBox}>
        <strong>Do not change settings here</strong> unless the Basketball Connect integration has failed for
        multiple people. Check with the admin first.
      </div>
    </div>
  ),
};

// ======================= release notes =======================
export const RELEASE_NOTES = (releaseHeading, dateBadge) => (
  <>
    <h3 style={releaseHeading}><span style={dateBadge}>4 Nov 2026</span>CC and BCC on SwishIQ Trials emails</h3>
    <ul style={ul}>
      <li>Every SwishIQ Trials email you send (Eliminated, Successful, player emails and Email Offer) now has <strong>CC</strong> and <strong>BCC</strong> boxes. Enter one or more addresses separated by commas (up to 10 each).</li>
      <li>The addresses are added to <strong>every</strong> email in the send, so a CC or BCC address receives one copy for each player. CC addresses are visible to the player; BCC addresses are hidden. They are recorded in Sent emails and in the Email Offer PDF.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>3 Nov 2026</span>Email Offer from the Outcome page</h3>
    <ul style={ul}>
      <li><strong>Email Offer</strong> (SwishIQ Trials → Sessions → Outcome): choose a team, fill out the offer template, preview the recipients (team, names, email addresses, and each email), extract the list as PDF or CSV, then Send after a final confirmation.</li>
      <li>The PDF list of the team is stored against the trial for auditing, and each offer sent is listed on the Outcome page and in Sent emails.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>5 Nov 2026</span>Team Assignments from BasketballConnect</h3>
    <ul style={ul}>
      <li>People linked to BasketballConnect are put on the <strong>Team Assignments</strong> of the teams they are registered in (after each sync, or with the <strong>BC Teams</strong> button). Only teams with games still to come; removed teams you took off by hand stay off.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>5 Nov 2026</span>Time off rejects clashing shifts; Shift Reliability report</h3>
    <ul style={ul}>
      <li><strong>Time off when already rostered</strong>: saving time off lists any games, sessions and shifts it clashes with and asks for confirmation. Confirming rejects them so they can be replaced; ones too close to start (or on a locked day) are left for the administrator.</li>
      <li><strong>Permanent record</strong>: every rejection (My Portal or time off) and, when a planner day is locked, every attended and no-show is kept, even if the shift is later removed.</li>
      <li><strong>Reports → Shift Reliability</strong>: who turns up and who does not, by date range, with rejections, no-shows, turn-up % and a CSV export.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>4 Nov 2026</span>Time off, and My Portal tabs follow Active roles</h3>
    <ul style={ul}>
      <li><strong>Time Off</strong> (new): book days off (one day, or 3 to 5 Nov) or a period within a day (3 Nov 9am to 5pm) on People → Availability or in My Portal → Availability. Nobody is rostered inside their time off: AI Allocate skips them and the planners mark them unavailable.</li>
      <li><strong>My Portal tabs</strong> now follow the roles a person is Active for: Game (referee or bench), Coach and Programs (coach), Court (court official). Summary and Availability always show.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>3 Nov 2026</span>Add Person from BasketballConnect, Team Exclusions, Team Assignments in My Portal</h3>
    <ul style={ul}>
      <li><strong>+ Add Person</strong> asks whether to select the person from BasketballConnect or add them manually.</li>
      <li><strong>Team Assignments</strong> moved from the Roles tab to the Availability tab, and are now editable by people in My Portal → Availability.</li>
      <li><strong>Team Exclusions</strong> (new): a person is never rostered on the games of a team they are excluded from. AI Allocate skips them and the Game Planner tags them (Excluded). Editable in People and in My Portal.</li>
      <li><strong>Active</strong> is ticked automatically when a qualification is added to a person (untick it if needed).</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>2 Nov 2026</span>Renamed to SwishIQ, with SwishIQ Trials and a new front door</h3>
    <ul style={ul}>
      <li><strong>New name</strong>: the Basketball Operations Management System (BOMS) is now <strong>SwishIQ</strong>. The former Swish IQ tab, rep-trial features and help are now <strong>SwishIQ Trials</strong>.</li>
      <li><strong>swishiq.snakepit.com.au</strong> now opens with two buttons: <strong>Trials</strong> (coach sign-in for trial sessions) and <strong>Management</strong> (roster.snakepit.com.au). Bookmark <strong>swishiq.snakepit.com.au/trials</strong> on shared iPads to go straight to the coach sign-in.</li>
      <li><strong>Messages</strong>: coach and My Portal text messages, emails and the sender name now say SwishIQ or SwishIQ Trials.</li>
      <li><strong>Coach mode</strong>: tiles can be coloured by team as well as status, with a key you can tap to filter; the Audit Log needs its permission ticked, even for Full Admin.</li>
    </ul>
    <h3 style={releaseHeading}><span style={dateBadge}>1 Oct 2026</span>Judiciary, SwishIQ Trials and team selection, People and Court updates</h3>
    <ul style={ul}>
      <li><strong>Judiciary tab</strong>: tech fouls by competition, team and player over a chosen number of weeks; raise, edit and cancel suspensions; red Suspension button on games.</li>
      <li><strong>Game reports</strong>: tech foul and injury names picked from both teams' players; tech foul severity (Low/High).</li>
      <li><strong>Basketball Connect</strong>: each player's team saved on every sync; names, email and mobile come from Basketball Connect; People matched to their Basketball Connect account (BC Match); children's email suffix removed.</li>
      <li><strong>SwishIQ Trials</strong>: Finalise Session (Continuation/Elimination, live list of players going through, non-progressing set to Unsuccessful); outcome counts, filters and Set all; teams and positions with a coloured summary; Team Selections report; Successful emails; Allow renumbering; finalised trial sessions locked.</li>
      <li><strong>Coach mode</strong>: SMS check for the recording coach; coach sign-in page at swishiq.snakepit.com.au; team and position picker; Current Teams; check-in positions and height; bigger tiles.</li>
      <li><strong>People</strong>: tabbed Edit Person window; BasketballConnect ID column; new tab order; Participants read-only BC fields and program multi-select.</li>
      <li><strong>Court</strong>: one day per row, name → role → time, shift and day costs, multiple shifts per person.</li>
      <li><strong>Admin</strong>: Coach Codes; SwishIQ Trials team/position defaults; BC Registrations create/edit/archive with the full forms; BC Integration warning.</li>
      <li><strong>My Portal</strong>: participant search on Programs; SwishIQ Trials in Summary; coach codes.</li>
    </ul>
  </>
);
